import React, { useEffect } from 'react';
import { colors, playful } from '../styles/theme';
import { AppStoreBadge, BetaInviteSection, FAQAccordion } from '../components';
import { FeatureCarousel } from '../components/FeatureCarousel';
import { homeFaqs, liveLanguages } from '../data';
import { useIsMobile } from '../hooks';
import { trackSectionView } from '../services/analytics';

export function HomePage() {
  const isMobile = useIsMobile(768);
  useEffect(() => {
    trackSectionView('home_hero', { creative_variant: 'hathi_carousel_v3' });
  }, []);
  return (
    <div>
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr',
          alignItems: 'center',
          gap: 30,
          padding: isMobile ? '24px 0 40px' : '48px 0 64px',
        }}
      >
        <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
          <h1
            style={{
              ...playful.heading,
              fontSize: 'clamp(2.35rem, 5vw, 4rem)',
            }}
          >
            Duolingo for
            <br />
            <span style={{ color: playful.orange }}>
              {liveLanguages.length} Indian Languages
            </span>
          </h1>
          <ul aria-label="Languages you can learn" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start', gap: 8, padding: 0, listStyle: 'none', margin: '24px 0' }}>
            {liveLanguages.map(language => <li key={language.code} style={{ display: 'flex', alignItems: 'center', gap: 7, background: language.background, border: '1px solid #e5e3dc', borderRadius: 999, padding: '7px 12px', fontSize: 14, fontWeight: 750 }}>
              <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: language.accent }} />{language.name}
            </li>)}
          </ul>
          <ol aria-label="How Bhol helps you speak" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, listStyle: 'none', padding: 0, margin: '24px 0 28px', textAlign: 'center' }}>
            {[
              { icon: <span style={{ fontSize: 28, fontWeight: 850 }}>Aa</span>, title: 'Read', detail: 'Words you can read', color: '#FFF0DF' },
              { icon: <svg width="38" height="34" viewBox="0 0 38 34" fill="none" aria-hidden="true"><path d="M4 13h7l9-8v24l-9-8H4z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/><path d="M26 11q6 6 0 12M31 6q10 11 0 22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>, title: 'Practise', detail: 'Listen and repeat', color: '#EAF6FF' },
              { icon: <img src="/conversation-icon.png" width={42} height={34} alt="" />, title: 'Talk', detail: 'Say it in a conversation', color: '#F0EDFF' },
            ].map((step, index) => <li key={step.title} style={{ ...playful.card, position: 'relative', background: step.color, padding: '16px 6px', borderRadius: 18 }}>
              <div aria-hidden="true" style={{ height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>{step.icon}</div>
              <strong style={{ display: 'block', fontSize: 16 }}>{step.title}</strong>
              <span style={{ display: 'block', fontSize: 12, color: colors.textLight, marginTop: 5 }}>{step.detail}</span>
              {index < 2 && <span aria-hidden="true" style={{ position: 'absolute', right: -13, top: 24, zIndex: 1, background: '#fff', borderRadius: '50%', width: 22, height: 22, fontWeight: 800 }}>→</span>}
            </li>)}
          </ol>
          <AppStoreBadge location="home_hero" />
        </div>
        <div
          style={{
            position: 'relative',
            maxWidth: 430,
            width: '100%',
            margin: '0 auto',
            padding: '24px 18px',
            background: '#FFF0DF',
            borderRadius: '45% 45% 36% 36%',
          }}
        >
          <div
            style={{
              ...playful.card,
              position: 'relative',
              width: 'fit-content',
              padding: '12px 22px',
              margin: '0 auto',
              color: playful.orange,
              fontSize: 23,
              fontWeight: 850,
              transform: 'rotate(-4deg)',
            }}
          >
            Let’s talk!
          </div>
          <img
            src="/head-hathi-512.webp"
            width={512}
            height={512}
            alt="Hathi, Bhol’s friendly elephant mascot"
            fetchPriority="high"
            loading="eager"
            style={{ display: 'block', width: '100%', height: 'auto' }}
          />
        </div>
      </section>
      <FeatureCarousel />
      <section
        style={{
          ...playful.card,
          textAlign: 'center',
          background: '#FFF0DF',
          padding: 'clamp(28px, 5vw, 52px)',
        }}
      >
        <h2
          style={{ ...playful.heading, fontSize: 'clamp(1.9rem, 4vw, 3rem)' }}
        >
          Try a lesson in Bhol.
        </h2>
        <p
          style={{
            color: colors.textLight,
            margin: '20px auto 24px',
            maxWidth: 470,
          }}
        >
          For the family call, the next trip, or the words you have been meaning
          to say.
        </p>
        <AppStoreBadge location="home_bottom_cta" />
      </section>
      <FAQAccordion faqs={homeFaqs} title="Questions about Bhol" />
      <section style={{ ...playful.card, padding: 'clamp(24px, 5vw, 40px)', background: '#EAF6FF', marginTop: 32 }}>
        <h2 style={{ ...playful.heading, fontSize: 'clamp(1.8rem, 4vw, 2.7rem)' }}>From the Bhol blog</h2>
        <p style={{ color: colors.textLight }}>Read pronunciation tips, phrase guides, and stories about learning Indian languages.</p>
        <a href="/blog" style={{ ...playful.smallButton, display: 'inline-block', textDecoration: 'none' }}>Explore the Bhol blog →</a>
      </section>
      <BetaInviteSection analyticsLocation="home_after_faq" />
    </div>
  );
}

export default HomePage;
